// Weather pill: temperature, humidity, where the air came from, and the alert
// tag. In a league there is a second line — the season's coming week — because
// the air a bird is carrying is planned days ahead, and a keeper who is about to
// move shade needs to know which day it is worth moving it for.

import { t } from '../../i18n';
import { chip } from './chip';
import { div, h, icon } from '../dom';
import type { ForecastCell, WeatherPillView } from '../selectors';
import type { WeatherCondition } from '../../core/types';

const THERMO_ICON = '<path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z"></path>';

/** One glyph per condition, so a week reads as a row of skies at a glance. */
const SKY_ICON: Record<WeatherCondition, string> = {
  CLEAR: '<circle cx="12" cy="12" r="4"></circle><path d="M12 2v3M12 19v3M2 12h3M19 12h3"></path>',
  CLOUDY: '<path d="M6 17a4 4 0 0 1 .5-8 5.5 5.5 0 0 1 10.6 1.4A3.5 3.5 0 0 1 17 17z"></path>',
  FOG: '<path d="M6 17a4 4 0 0 1 .5-8 5.5 5.5 0 0 1 10.6 1.4A3.5 3.5 0 0 1 17 17z"></path><path d="M5 20h14M7 23h10"></path>',
  RAIN: '<path d="M6 15a4 4 0 0 1 .5-8 5.5 5.5 0 0 1 10.6 1.4A3.5 3.5 0 0 1 17 15z"></path><path d="M8 18l-1 3M13 18l-1 3M18 18l-1 3"></path>',
  STORM: '<path d="M6 15a4 4 0 0 1 .5-8 5.5 5.5 0 0 1 10.6 1.4A3.5 3.5 0 0 1 17 15z"></path><path d="M13 16l-3 5h4l-2 4"></path>',
};

export function weatherPill(view: WeatherPillView): HTMLElement {
  const pill = div(
    { class: 'cf-weather' },
    icon({ shapes: THERMO_ICON, size: 20, strokeWidth: 2 }),
    h('span', { class: 'cf-weather__temp', text: view.temp }),
    h('span', { class: 'cf-weather__place', text: view.location }),
  );
  if (view.humidity) pill.appendChild(h('span', { class: 'cf-weather__extra', text: view.humidity }));
  if (view.alertKey) {
    pill.appendChild(chip({ text: t(view.alertKey), tone: view.alertTone === 'danger' ? 'danger' : 'warn' }));
  }
  // Normal has no league, so it gets the one line it has always got.
  if (!view.outlook) return pill;
  return div({ class: 'cf-stack cf-stack--tight' }, pill, weekStrip(view.outlook));
}

function weekStrip(cells: ForecastCell[]): HTMLElement {
  const strip = div({
    class: 'cf-week',
    attrs: { role: 'list', 'aria-label': t('weather.outlook') },
  });
  for (const cell of cells) {
    strip.appendChild(
      div(
        {
          class: `cf-week__day${cell.storm ? ' cf-week__day--storm' : ''}`,
          attrs: { role: 'listitem' },
        },
        h('span', { class: 'cf-week__n', text: `D${cell.leagueDay + 1}` }),
        // The icon takes a tone rather than inheriting the cell's colour, so a
        // day under a watch is marked on the sky and not only on the number.
        icon({ shapes: SKY_ICON[cell.condition], size: 18, strokeWidth: 2, tone: cell.storm ? 'danger' : 'muted' }),
        h('span', { class: 'cf-week__high', text: cell.high }),
      ),
    );
  }
  return strip;
}
